import { confirmMtaPolicy } from '@folio/utils/confirmMtaPolicy';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { offlineAwarePaymentMethodRule } from '@folio/utils/offlineAwarePaymentMethodRule';
import { DocumentsListsPolicyModule, InvoicesScheduleModule } from '@korint/folio';
import { policyControllerPushPolicyEvent } from 'korint-sdk';
import type {
  ConditionalVisibility,
  Field,
  FieldShortcut,
  FormSections,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { BriefcaseIcon } from 'lucide-react';
import { retailSubscriptionFeeSection } from '../configs/retailSubscriptionFeeSection';
import { MultiRisquesProBillingStep } from '../custom-pages/MultiRisquesProBillingStep';
import { MultiRisquesProClaimsStep } from '../custom-pages/MultiRisquesProClaimsStep';
import { PolicyholderConfirmationStep } from '../custom-pages/PolicyholderConfirmationStep';
import { PolicyholderSummaryStep } from '../custom-pages/PolicyholderSummaryStep';
import { PolicyWakamErrorBanner } from '../custom-pages/PolicyWakamErrorBanner';
import { QuoteWakamError } from '../custom-pages/QuoteWakamError';

const stopReasonsMapping = {
  VOID: 'Nullité de contrat',
  RISK_AGGRAVATION: 'Aggravation du risque',
  AMICABLE: 'Amiable',
  ACTIVITY_CESSATION: "Cessation d'activité",
  COMPANY_CHANGE: 'Changement de compagnie',
  DEATH: 'Décès',
  MISSING_DOCUMENTS: 'Documents manquants',
  DESTRUCTION: 'Destruction',
  MAIN_DUE_DATE: 'Échéance principale',
  FALSE_DECLARATION: 'Fausse déclaration',
  CHATEL_LAW: 'Loi Chatel',
  HAMON_LAW: 'Loi Hamon',
  NON_PAYMENT: 'Non paiement',
  RADIATION: 'Radiation',
  CONTRACT_REPLACEMENT: 'Remplacement de contrat',
  COMPANY_TERMINATION: 'Résiliation compagnie',
  VEHICLE_RETURN: 'Restitution véhicule',
  LICENSE_WITHDRAWAL: 'Retrait de permis',
  NO_EFFECT: 'Sans effet',
  CLAIMS: 'Sinistralité',
  SALE: 'Vente',
  THEFT: 'Vol',
  OTHER: 'Autre',
  REFUSAL_TO_INCREASE_PREMIUM: "Refus d'augmentation de prime",
  WAIVER: 'Renonciation (14j)',
};

const policyHolderInvitationRole = ['DRIVER'];

const showWhenCompanyExists = {
  fieldName: 'isCompanyBeingCreated',
  condition: 'equals',
  value: 'false',
} as const satisfies ConditionalVisibility;

const showWhenCompanyBeingCreated = {
  fieldName: 'isCompanyBeingCreated',
  condition: 'equals',
  value: 'true',
} as const satisfies ConditionalVisibility;

const companySections = [
  {
    title: 'Entreprise bénéficiaire',
    fields: [
      { name: 'isCompanyBeingCreated' },
      { name: 'companyCustomName', showWhen: [showWhenCompanyBeingCreated] },
      {
        name: 'companySiret',
        required: true,
        type: 'company',
        displayCompanyInformation: true,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                },
                nafLabel: apiData?.customFields?.companyNafLabel,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        isCustomField: true,
        showWhen: [showWhenCompanyExists],
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const solvabilitySections = [
  {
    title: 'Évaluation de solvabilité',
    description: 'Ce score a une influence sur la tarification',
    fields: [
      { name: 'solvabilityScore', type: 'readonly', required: false },
      { name: 'solvabilityRiskClassComment', type: 'readonly' },
      { name: 'solvabilityScoreComment', type: 'readonly' },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const showWhenDependencyNumberIsGreaterThanZero = {
  fieldName: 'dependencyNumber',
  condition: 'greaterThan',
  value: 0,
} as const satisfies ConditionalVisibility;

const showWhenLargeShoppingCenterComplianceIsFalse = {
  fieldName: 'largeShoppingCenterCompliance',
  condition: 'equals',
  value: false,
} as const satisfies ConditionalVisibility;

const showWhenOccupationQualityIsTenant = {
  fieldName: 'occupationQuality',
  condition: 'equals',
  value: 'TENANT',
} as const satisfies ConditionalVisibility;

const assetFields = [
  'name',
  'annualRevenueExcludingTax',
  'occupationQuality',
  { name: 'premisesAddress', type: 'address' },
  { name: 'premisesPostcode', maxLength: 5 },
  { name: 'premisesCity', type: 'city' },
  {
    name: 'premisesCountry',
    // Backend defaultValue only applies on asset creation, which this step does not do.
    apiDataAccessor: () => 'France',
    disabled: true,
  },
  'premisesArea',
  { name: 'premisesContentValue', min: 0 },
  { name: 'staffCount', min: 0 },
  { name: 'activity1', searchable: true },
  { name: 'activity2', clearable: true, searchable: true },
  { name: 'activity3', clearable: true, searchable: true },
  { name: 'dependencyNumber' },
  { name: 'dependencyAdress', showWhen: [showWhenDependencyNumberIsGreaterThanZero] },
  {
    name: 'dependencyAdressCompliance',
    type: 'checkbox',
    required: true,
    showWhen: [showWhenDependencyNumberIsGreaterThanZero],
  },
  { name: 'largeShoppingCenterCompliance', required: false, type: 'checkbox' },
  {
    name: 'commercialLeaseWaiverOfRecourse',
    showWhen: [showWhenLargeShoppingCenterComplianceIsFalse, showWhenOccupationQualityIsTenant],
  },
] as const satisfies FieldShortcut[];

const signatorySections = [
  {
    fields: [
      'signatoryCivility',
      'signatoryLastName',
      'signatoryFirstName',
      'signatoryDateOfBirth',
      'signatoryCountryOfBirth',
      'signatoryEmail',
      'signatoryPhoneNumber',
      'signatoryFunction',
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const mandatoryDeclarationsFields = [
  {
    name: 'businessHoursCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'constructionCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'nonInsurableAreas',
    description:
      "Notamment et sans s'y limiter dans les rues Des rosiers, Jean Henri Fabre et/ou Jules Valiès à Saint Ouen (93400), ou à proximité directe des marchés de Biron, Cambo, Ottino, Paul Bert, Vernaison à Saint Ouen (93400) ou sur l'avenue du Professeur André-Lemierre à Paris 20 e arrondissement (75020) ou dans la rue Eugène Pottier à Villeurbanne (69100) ou sur l'avenue Georges Lefenestre à Paris 14e arrondissement (75014) ou sur la Place d'Armes à Belfort (90000) ou sur le quai Carnot à Montsorreau (49730) ou sur la Cours Saleva à Nice (06300) ou sur la Place Saint Nicolas Bastia (20200)",
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'nightclubCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'highRisePropertyCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'historicMonumentCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'combustiblePackagingCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'grossistOrManufacturerCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'floodZoneCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'seasonalBusinessCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
    description: 'Une activité saisonnière est une activité ouverte maximum 9 mois par an',
  },
  {
    name: 'highRiskCommercialZoneCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
    description:
      "C'est à dire zone industrielle (ZI), zone artisanale (ZA) et/ou zone d'activité économique (ZAE) ne faisant l'objet d'aucune mesure de sécurité (alarme, vidéosurveillance, grilles de sécurité, gardiennage permanent individuel ou collectif) ou hors d'une zone d'activité commerciale sans mesure de sécurité (celles citées ci-dessus) et situés à plus de 10 minutes en voiture des pompiers et 100m de bâtiments d'habitation normalement occupés",
  },
  {
    name: 'securityCompliance',
    required: true,
    type: 'checkbox',
    isCustomField: true,
    description: (
      <>
        C'est à dire zone identifiée comme sensible (« quartier prioritaire ») selon le site suivant
        :{' '}
        <u>
          <a href="https://sig.ville.gouv.fr/" target="_blank" rel="noopener noreferrer">
            https://sig.ville.gouv.fr/
          </a>
        </u>
      </>
    ),
  },
] as const satisfies Array<Field>;

const holderDeclarationsFields = [
  {
    name: 'terminationForClaims',
    required: false,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'terminationForNonPaymentType',
    required: true,
    type: 'radio',
    isCustomField: true,
    options: [
      { value: 'RECIDIVIST', label: 'Avec récidive' },
      { value: 'NON_RECIDIVIST', label: 'Sans récidive' },
      {
        value: 'NO_TERMINATION',
        label:
          "Le titulaire du contrat n'a PAS fait l'objet de résiliation pour non paiement dans les 36 derniers mois",
      },
    ],
  },
  {
    name: 'insuredInLastSixMonths',
    required: false,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'liquidationOrRecoveryCompliance',
    required: false,
    type: 'checkbox',
    isCustomField: true,
  },
] as const satisfies Array<Field>;

const declarationsSections: FormSectionsWithShortcuts = [
  {
    title: 'Déclarations obligatoires',
    fields: mandatoryDeclarationsFields,
    enableApproveAllButton: true,
    description: 'Le souscripteur déclare que :',
  },
  {
    title: 'Déclarations du titulaire du contrat',
    fields: holderDeclarationsFields,
  },
];

const clausierSections: FormSectionsWithShortcuts = [
  {
    title: 'Dérogation',
    description:
      'Renseignez ce champ uniquement si vous souhaitez formuler une demande spécifique. Toute clause indiquée sera traitée en dérogation.',
    fields: [
      {
        name: 'createDerogationForClausier',
        isCustomField: true,
        type: 'checkbox',
        required: false,
      },
      {
        name: 'clausierComment',
        isCustomField: true,
        type: 'textarea',
        required: false,
        showWhen: [{ fieldName: 'createDerogationForClausier', condition: 'equals', value: true }],
        formDataAccessor: formData =>
          formData.clausierComment === '' ? undefined : formData.clausierComment,
      },
    ],
  },
];

const managementFeeField: Field = {
  name: 'managementFeeTaxesIncluded',
  isCustomField: true,
  type: 'number',
  required: true,
  min: 0,
  apiDataAccessor: apiData => (apiData.customFields.managementFeeTaxesIncluded ?? 0) / 100,
  formDataAccessor: formData => Number(formData.managementFeeTaxesIncluded ?? 0) * 100,
};

const newBusinessQuotePolicySections: FormSections = [
  {
    title: 'Frais de dossier et commissions du distributeur',
    fields: [
      managementFeeField,
      {
        name: 'retailFee',
        required: true,
        type: 'number',
        integer: true,
        min: 1,
        max: 18,
        apiDataAccessor: apiData =>
          apiData.brokerageFees?.find(
            ({ brokerageFirmId }: { brokerageFirmId: string; fee: number }) =>
              brokerageFirmId === apiData.brokerageFirm,
          )?.fee ?? 0,
        formDataAccessor: formData => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.retailFee ?? 0),
            },
          ],
        }),
      },
    ],
  },
];

const mtaQuotePolicySections: FormSections = [
  { title: 'Frais de dossier', fields: [managementFeeField] },
];

const quoteAssetSections: FormSections = [
  {
    title: 'Date de début de contrat',
    fields: [{ name: 'startedAt', type: 'datetime', required: true, min: 'now' }],
  },
];

export const AxeceMultiRisquesProConfig = {
  display: {
    category: 'professionnel',
    icon: BriefcaseIcon,
    badges: [],
  },
  productCustomPath: '/multi-risques-pro',
  enableAssetComparison: true,
  policyWarningBanners: [PolicyWakamErrorBanner],
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  discoveryFunnelSteps: {
    steps: [
      { type: 'documentUploadFunnel', path: 'documents-upload', context: 'policy' },
      { type: 'customerFunnel', path: 'customer', sections: companySections },
      { type: 'customerFunnel', path: 'solvability', sections: solvabilitySections },
      { type: 'assetFunnel', path: 'assets', fields: assetFields, minAssets: 1, maxAssets: 3 },
      // Every claim must carry its responsibility before Next, which `module` cannot express.
      { type: 'custom', path: 'claims', component: <MultiRisquesProClaimsStep /> },
      { type: 'customerFunnel', path: 'signatory', sections: signatorySections },
      { type: 'policyFunnel', path: 'declarations', sections: declarationsSections },
      // The annual-only alert reads the customer and policy, which the declarative step cannot.
      { type: 'custom', path: 'billing-frequency', component: <MultiRisquesProBillingStep /> },
      { type: 'policyFunnel', path: 'clausier', sections: clausierSections },
      {
        type: 'quoteFunnel',
        path: 'quote',
        additionalComponent: <QuoteWakamError />,
        policySections: newBusinessQuotePolicySections,
        assetSections: quoteAssetSections,
        additionalQuoteComponentsSections: [retailSubscriptionFeeSection],
      },
      { type: 'module', path: 'invoicing-schedule', module: <InvoicesScheduleModule /> },
      {
        type: 'funnelEnd',
        path: 'end',
        id: 'new-business-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          showDocumentsGeneratedAtQuote: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'AXECE_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  // `mta-end` rather than `end`: uniq gives every funnel of a product one i18n namespace.
  mtaFunnelSteps: {
    steps: [
      { type: 'newMTACreation', path: 'new' },
      { type: 'assetFunnel', path: 'assets', fields: assetFields, minAssets: 1, maxAssets: 3 },
      { type: 'custom', path: 'claims', component: <MultiRisquesProClaimsStep /> },
      { type: 'customerFunnel', path: 'signatory', sections: signatorySections },
      { type: 'policyFunnel', path: 'declarations', sections: declarationsSections },
      {
        type: 'quoteFunnel',
        path: 'quote',
        additionalComponent: <QuoteWakamError />,
        policySections: mtaQuotePolicySections,
        assetSections: quoteAssetSections,
      },
      { type: 'module', path: 'invoicing-schedule', module: <InvoicesScheduleModule /> },
      {
        type: 'funnelEnd',
        path: 'mta-end',
        id: 'mta-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          showDocumentsGeneratedAtQuote: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await confirmMtaPolicy(policyId, branchId);
          },
        },
      },
    ],
  },
  // `confirmation` rather than `end`, for the same shared-namespace reason as `mta-end`.
  policyholderDiscoveryFunnelSteps: {
    steps: [
      { type: 'documentUploadFunnel', path: 'documents-upload', context: 'policy' },
      { type: 'custom', path: 'summary', component: <PolicyholderSummaryStep /> },
      { type: 'paymentMethodFunnel', path: 'payment-method' },
      {
        type: 'module',
        path: 'documents',
        module: <DocumentsListsPolicyModule displayContext="policyholder" />,
      },
      { type: 'intermediarySignatureFunnel', path: 'signature', displayContext: 'policyholder' },
      { type: 'custom', path: 'confirmation', component: <PolicyholderConfirmationStep /> },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      {
        key: 'companyCustomName',
        hideIfEmpty: true,
        contactKey: ContactKey.TEMPORARY_CUSTOMER_NAME,
      },
      {
        key: 'signatoryLastName',
        contactKey: ContactKey.LAST_NAME,
      },
      {
        key: 'signatoryFirstName',
        contactKey: ContactKey.FIRST_NAME,
      },
      {
        key: 'signatoryDateOfBirth',
      },
      {
        key: 'signatoryCountryOfBirth',
      },
      {
        key: 'signatoryEmail',
      },
      {
        key: 'signatoryPhoneNumber',
      },
      {
        key: 'signatoryFunction',
      },
      {
        key: 'companyName',
        contactKey: ContactKey.COMPANY_NAME,
      },
      {
        key: 'companySiret',
        hideIfEmpty: true,
      },
      {
        key: 'companyAddress',
      },
      {
        key: 'companyCountry',
      },
      {
        key: 'companyCity',
      },
      {
        key: 'companyCreationDate',
      },
      {
        key: 'companyPostcode',
      },
      {
        key: 'solvabilityScore',
      },
      {
        key: 'solvabilityRiskClassComment',
        hideIfEmpty: true,
      },
      {
        key: 'solvabilityScoreComment',
        hideIfEmpty: true,
      },
    ],
  },
  policyInformationCard: {
    title: 'Information de la police',
    fieldsConfig: [
      {
        key: 'clausierComment',
      },
    ],
  },
  assetInformations: {
    fieldsConfig: [
      'annualRevenueExcludingTax',
      {
        key: 'occupationQuality',
      },
      'premisesAddress',
      'premisesCountry',
      'premisesPostcode',
      'premisesCity',
      'premisesArea',
      'premisesContentValue',
      'staffCount',
      {
        key: 'activity1',
      },
      {
        key: 'activity2',
        hideIfEmpty: true,
      },
      {
        key: 'activity3',
        hideIfEmpty: true,
      },
      'dependencyNumber',
      {
        key: 'dependencyAdress',
        hideIfEmpty: true,
      },
      {
        key: 'largeShoppingCenterCompliance',
        type: 'boolean',
        formatValue: (value: string | number | boolean | undefined) => {
          return value === false ? 'Oui' : value === true ? 'Non' : undefined;
        },
        hideIfEmpty: true,
      },
      {
        key: 'commercialLeaseWaiverOfRecourse',
        hideIfEmpty: true,
      },
    ],
  },
  claims: {
    claimsCategories: [
      {
        category: 'FIRE_AND_EXPLOSION',
        label: 'Incendies, explosions et événements assimilés',
      },
      {
        category: 'ELECTRICAL_DAMAGE',
        label: 'Dommages électriques',
      },
      {
        category: 'STORED_GOODS',
        label: 'Marchandises conservées',
      },
      {
        category: 'WATER_DAMAGE',
        label: 'Dégâts des eaux',
      },
      {
        category: 'THEFT_VANDALISM_ATTEMPTED_THEFT',
        label: 'Vol, vandalisme et tentative de vol',
      },
      {
        category: 'NATURAL_DISASTERS',
        label: 'Catastrophes Naturelles',
      },
      {
        category: 'EQUIPMENT_BREAKDOWN',
        label: 'Bris de matériel',
      },
      {
        category: 'DEFENSE_AND_SUBROGATION',
        label: 'Défense pénale et recours',
      },
      {
        category: 'PROFESSIONAL_PREMISES_LIABILITY',
        label: 'Responsabilité civile',
      },
      {
        category: 'GLASS_BREAKAGE',
        label: 'Bris de glace',
      },
      {
        category: 'OPERATING_LOSS',
        label: "Perte d'exploitation",
      },
      {
        category: 'LEGAL_PROTECTION',
        label: 'Protection juridique',
      },
      {
        category: 'PERMANENT_VALUE_LOSS',
        label: 'Perte de valeur vénale',
      },
    ],
    fieldsConfig: [
      {
        name: 'amount',
        type: 'number',
        required: true,
        isCustomField: true,
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: true,
    claimsReview: { attachedTo: 'assets' },
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/multi-risques-pro/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/multi-risques-pro/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: false,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  priceBreakdownConfig: {
    groups: [
      {
        lines: [
          { key: 'PREMIUM_PRE_TAXES' },
          { key: 'PREMIUM_TAXES' },
          { key: 'TOTAL_PREMIUM', variant: 'bold' },
          { key: 'TOTAL_PREMIUM_RETAIL', variant: 'bold' },
          { key: 'MANAGEMENT_FEE' },
          { key: 'MANAGEMENT_FEE_RETAIL' },
          { key: 'SUBSCRIPTION_FEE', tooltip: true },
          { key: 'SUBSCRIPTION_FEE_RETAIL', tooltip: true },
          { key: 'ATTACK_TAX', tooltip: true },
        ],
      },
    ],
    quoteRecapOverride: [
      {
        lines: [
          { key: 'QUOTE_RECAP_TOTAL_PREMIUM', variant: 'bold' },
          { key: 'QUOTE_RECAP_TOTAL_PREMIUM_RETAIL', variant: 'bold' },
          { key: 'MANAGEMENT_FEE' },
          { key: 'MANAGEMENT_FEE_RETAIL' },
          { key: 'ATTACK_TAX' },
        ],
      },
    ],
  },
  stopReasonsMapping,
  finalizeNewBusinessModuleConfig: {
    rules: [
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: offlineAwarePaymentMethodRule({ requiredType: 'sepa_debit' }),
      },
      {
        id: 'company-creation',
        isValid: ({ customer }) => customer.customFields?.isCompanyBeingCreated === false,
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/informations`,
      },
    ],
  },
} as const satisfies KorintModulesProductsConfig;
